import { createFileRoute, useNavigate, redirect, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { verifyRecaptcha } from "@/lib/recaptcha.functions";
import { useServerFn } from "@tanstack/react-start";
import { sendEmail } from "@/lib/email/send.functions";
import { checkDuplicateApplication } from "@/lib/auth.functions";
import { FlipWords } from "@/components/flip-words";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { CodeOfConductBody, COC_VERSION } from "@/components/code-of-conduct";

const CATEGORIES = [
  "Womens Fashion",
  "Beauty & Skincare",
  "Mother & Baby",
  "Home & Decor",
  "Fitness & Wellness",
  "Shoes & Accessories",
  "Cooking & Grocery",
  "Finance & Lifestyle",
  "Travel",
  "GRWM",
] as const;
type Category = (typeof CATEGORIES)[number];

export const Route = createFileRoute("/apply")({
  validateSearch: (search: Record<string, unknown>) => ({
    ref: typeof search.ref === "string" ? search.ref.slice(0, 32) : undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: "/login", search: { redirect: location.href } });
  },
  head: () => ({
    meta: [
      { title: "Become a Creator on Pluggz — Apply Now" },
      { name: "description", content: "Join Pluggz as a verified creator. Build your own shoppable storefront, curate products you love and earn commission on every sale. Apply today." },
      { property: "og:title", content: "Become a Creator on Pluggz — Apply Now" },
      { property: "og:description", content: "Join Pluggz as a verified creator. Build your own shoppable storefront, curate products you love and earn commission on every sale. Apply today." },
      { property: "og:url", content: `${SITE_URL}/apply` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/apply` }],
  }),
  component: Apply,
});

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
}

function Apply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { ref } = Route.useSearch();

  const existing = useQuery({
    queryKey: ["my-creator", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (existing.isLoading) {
    return <div className="mx-auto max-w-2xl px-6 py-20 text-sm text-muted-foreground">Loading…</div>;
  }

  if (existing.data) {
    return <StatusView creator={existing.data} />;
  }

  return <ApplicationForm referredByCode={ref ?? null} onSuccess={() => { qc.invalidateQueries({ queryKey: ["my-creator"] }); navigate({ to: "/creator-dashboard" }); }} />;
}

function StatusView({ creator }: { creator: any }) {
  const status = creator.status as "pending" | "approved" | "live" | "rejected";
  const meta: Record<string, { label: string; tone: string; copy: string }> = {
    pending: { label: "Under review", tone: "bg-amber-100 text-amber-900", copy: "Our team reviews new applications weekly. You'll hear back by email." },
    approved: { label: "Approved", tone: "bg-emerald-100 text-emerald-900", copy: "Welcome aboard. Start curating products to take your storefront live." },
    live: { label: "Live", tone: "bg-emerald-700 text-white", copy: "Your storefront is published. Keep curating to grow your earnings." },
    rejected: { label: "Not a fit (yet)", tone: "bg-rose-100 text-rose-900", copy: "Thanks for applying. We're not onboarding in your category right now." },
  };
  const m = meta[status];

  return (
    <div className="mx-auto max-w-2xl px-6 py-20">
      <p className="editorial-eyebrow">Your application</p>
      <h1 className="mt-2 font-serif text-5xl">{creator.name}</h1>
      <p className="mt-2 text-sm text-muted-foreground">@{creator.handle}</p>

      <div className={`mt-8 inline-flex items-center px-3 py-1 text-xs uppercase tracking-widest ${m.tone}`}>{m.label}</div>
      <p className="mt-4 text-muted-foreground">{m.copy}</p>

      <dl className="mt-10 grid gap-4 border-t border-border pt-8 text-sm">
        <Row label="Bio" value={creator.bio} />
        <Row label="Followers" value={creator.followers} />
        <Row label="Categories" value={(creator.categories ?? []).join(", ") || "—"} />
        <Row label="Pitch" value={creator.pitch} />
        <Row label="Instagram" value={creator.instagram} />
        <Row label="TikTok" value={creator.tiktok} />
        <Row label="Website" value={creator.website} />
      </dl>

      {(status === "approved" || status === "live") && (
        <div className="mt-10 flex gap-3">
          <Link to="/creator-dashboard" className="bg-foreground px-6 py-3 text-xs uppercase tracking-widest text-background">Open dashboard</Link>
          {status === "live" && (
            <Link to="/creator/$handle" params={{ handle: creator.handle }} className="border border-border px-6 py-3 text-xs uppercase tracking-widest">View storefront</Link>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-4">
      <dt className="truncate text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value || "—"}</dd>
    </div>
  );
}

function ApplicationForm({ onSuccess, referredByCode }: { onSuccess: () => void; referredByCode: string | null }) {
  const { user } = useAuth();
  const verify = useServerFn(verifyRecaptcha);
  const email = useServerFn(sendEmail);
  const checkDup = useServerFn(checkDuplicateApplication);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [bio, setBio] = useState("");
  const [followers, setFollowers] = useState("");
  const [pitch, setPitch] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [website, setWebsite] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [touchedHandle, setTouchedHandle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [cocAccepted, setCocAccepted] = useState(false);

  useEffect(() => {
    if (!touchedHandle) setHandle(slugify(name));
  }, [name, touchedHandle]);

  function toggleCategory(c: Category) {
    setCategories((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  }

  function validateStep(s: 1 | 2 | 3): string | null {
    if (s === 1) {
      if (name.trim().length < 2) return "Name is too short.";
      if (handle.length < 3) return "Handle must be at least 3 characters.";
      if (bio.trim().length < 20) return "Bio should be at least 20 characters.";
    }
    if (s === 3) {
      if (categories.length === 0) return "Pick at least one category.";
    }
    return null;
  }

  function next() {
    const err = validateStep(step);
    if (err) return setError(err);
    setError(null);
    setStep((s) => (s === 1 ? 2 : 3));
  }

  function back() {
    setError(null);
    setStep((s) => (s === 3 ? 2 : 1));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (name.trim().length < 2) return setError("Name is too short.");
    if (handle.length < 3) return setError("Handle must be at least 3 characters.");
    if (bio.trim().length < 20) return setError("Bio should be at least 20 characters.");
    if (categories.length === 0) return setError("Pick at least one category.");
    if (!cocAccepted) return setError("Please agree to the Pluggz Creator Code of Conduct.");

    setLoading(true);
    setError(null);
    const token = await getRecaptchaToken("apply");
    const captcha = await verify({ data: { token, action: "apply" } });
    if (!captcha.ok) {
      setLoading(false);
      return setError("Couldn't verify you're human. Try again.");
    }

    const dup = await checkDup({ data: { handle: slugify(handle) } });
    if (!dup.ok) {
      setLoading(false);
      return setError(dup.error);
    }

    const { error } = await supabase.from("creators").insert({
      user_id: user.id,
      handle: slugify(handle),
      name: name.trim(),
      bio: bio.trim(),
      followers: followers.trim() || null,
      pitch: pitch.trim() || null,
      instagram: instagram.trim() || null,
      tiktok: tiktok.trim() || null,
      website: website.trim() || null,
      categories,
      status: "pending",
      coc_accepted_at: new Date().toISOString(),
      coc_version: COC_VERSION,
      referred_by_code: referredByCode ?? null,
    });
    setLoading(false);
    if (error) {
      setError(error.message.includes("duplicate") ? "That handle is taken — try another." : error.message);
      return;
    }
    email({ data: { to: user.email!, template: { name: "welcome", data: { name: name.trim() } } } }).catch(() => {});
    onSuccess();
  }

  const steps = [
    { n: 1 as const, label: "Storefront" },
    { n: 2 as const, label: "Audience" },
    { n: 3 as const, label: "Preferences" },
  ];

  return (
    <div className="relative mx-auto max-w-2xl px-6 py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-accent/15 blur-3xl float-slow" />
      <p className="editorial-eyebrow">Become a creator</p>
      <h1 className="mt-2 font-serif text-5xl">
        Open your{" "}
        <FlipWords
          words={["storefront", "boutique", "shop", "gallery", "collection"]}
          className="italic shimmer"
          duration={3000}
        />
      </h1>
      <TextGenerateEffect
        words="Tell us about yourself and what you'd love to curate. We review applicants weekly."
        className="mt-4 text-muted-foreground"
        duration={0.4}
        staggerDelay={0.04}
      />

      {referredByCode && (
        <div className="mt-6 border border-border bg-accent/10 px-4 py-3 text-sm">
          You were referred by a Pluggz creator — you're in good hands 👋
        </div>
      )}

      <div className="mt-10 flex items-center">
        {steps.map((s, i) => {
          const done = step > s.n;
          const active = step === s.n;
          const filled = done || active;
          return (
            <div key={s.n} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs transition-colors ${filled ? "border-foreground bg-foreground text-background" : "border-border bg-background text-muted-foreground"}`}>
                  {s.n}
                </div>
                <span className={`mt-2 text-[10px] uppercase tracking-widest ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`mx-3 mb-6 h-px flex-1 ${step > s.n ? "bg-foreground" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {step === 1 && (
        <Section title="Storefront">
          <Field label="Display name">
            <input required value={name} onChange={(e) => setName(e.target.value)} maxLength={60} className={input} />
          </Field>
          <Field label="Handle" hint="Lowercase letters, numbers and dashes. Used in your URL.">
            <div className="flex items-center border border-border bg-card">
              <span className="px-3 text-xs text-muted-foreground">pluggzofficial.com/creator/</span>
              <input required value={handle} onChange={(e) => { setTouchedHandle(true); setHandle(slugify(e.target.value)); }} maxLength={32} className="w-full bg-transparent py-3 pr-4 text-sm outline-none" />
            </div>
          </Field>
          <Field label="Short bio">
            <textarea required value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={400} className={input} />
            <p className="mt-1 text-xs text-muted-foreground">{bio.length}/400</p>
          </Field>
        </Section>
        )}

        {step === 2 && (
        <Section title="Audience">
          <Field label="Follower count" hint="Across your largest channel (e.g. 120K).">
            <input value={followers} onChange={(e) => setFollowers(e.target.value)} maxLength={20} className={input} />
          </Field>
          <Field label="Instagram"><input value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@handle" maxLength={60} className={input} /></Field>
          <Field label="TikTok"><input value={tiktok} onChange={(e) => setTiktok(e.target.value)} placeholder="@handle" maxLength={60} className={input} /></Field>
          <Field label="Website"><input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" maxLength={200} className={input} /></Field>
        </Section>
        )}

        {step === 3 && (
        <Section title="Product preferences">
          <Field label="Categories you'd curate" hint="Pick everything that fits your audience.">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const on = categories.includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleCategory(c)} className={`border px-4 py-2 text-xs uppercase tracking-widest transition-colors ${on ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Your pitch" hint="Brands you love, your aesthetic, why your audience would shop here.">
            <textarea value={pitch} onChange={(e) => setPitch(e.target.value)} rows={4} maxLength={600} className={input} />
            <p className="mt-1 text-xs text-muted-foreground">{pitch.length}/600</p>
          </Field>
        </Section>
        )}

        {step === 3 && (
          <Section title="Creator Code of Conduct">
            <div className="max-h-64 overflow-y-auto border border-border rounded p-4 bg-card">
              <CodeOfConductBody />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={cocAccepted}
                onChange={(e) => setCocAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <span className="text-sm">I agree to the Pluggz Creator Code of Conduct</span>
            </label>
          </Section>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="flex gap-3">
          {step > 1 && (
            <button type="button" onClick={back} className="flex-1 border border-border px-6 py-4 text-sm uppercase tracking-widest hover:bg-card transition-colors">
              Back
            </button>
          )}
          {step < 3 ? (
            <button type="button" onClick={next} className="flex-1 bg-foreground px-6 py-4 text-sm uppercase tracking-widest text-background">
              Next
            </button>
          ) : (
            <button disabled={loading} type="submit" className="flex-1 bg-foreground px-6 py-4 text-sm uppercase tracking-widest text-background disabled:opacity-50">
              {loading ? "Submitting…" : "Submit application"}
            </button>
          )}
        </div>
        <div className="text-center">
          <Link to="/application-status" className="text-xs text-muted-foreground hover:text-foreground link-underline">
            Check your application status
          </Link>
        </div>
      </form>
    </div>
  );
}

const input = "w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:border-foreground";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-4 border-t border-border pt-6">
      <legend className="editorial-eyebrow">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </label>
  );
}
